import {
  UnifiedMonitoringMessageType,
  UnifiedMonitoringOperatorPerformanceMessage,
  WsConnectionStatus,
} from '@hakimo-ui/hakimo/types';
import { useUnifiedMonitoringWs } from '@hakimo-ui/hakimo/util';
import { useEffect, useRef, useState } from 'react';
import { useUnifiedMonitoringWsMessageHandler } from '../hooks/useUnifiedMonitoringWsMessageHandler';
import { MINUTE_MS } from './utils';

/**
 * Requests the operator's panel each time the socket (re)connects; event-flow
 * pushes a fresh one after every Pulse cycle, so there is no polling. Call it
 * where it stays mounted for the whole page, so pushes that arrive while the
 * chip is not shown are kept.
 */
export function useOperatorPerformance() {
  const { connectionState, send } = useUnifiedMonitoringWs();
  const [message, setMessage] =
    useState<UnifiedMonitoringOperatorPerformanceMessage | null>(null);

  // send is recreated on every provider render; only a (re)connect should
  // trigger a request.
  const sendRef = useRef(send);
  sendRef.current = send;
  const isConnected = connectionState.status === WsConnectionStatus.CONNECTED;
  useEffect(() => {
    if (isConnected) {
      sendRef.current?.({
        event_type: UnifiedMonitoringMessageType.GET_OPERATOR_PERFORMANCE,
      });
    }
  }, [isConnected]);

  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.OPERATOR_PERFORMANCE,
    setMessage
  );

  return message;
}

/** Wall-clock time, re-read every minute and whenever `message` changes. */
export function useMinuteClock(message: unknown) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
  }, [message]);

  useEffect(() => {
    const timerId = window.setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => clearInterval(timerId);
  }, []);

  return now;
}
